import { useEffect, useRef } from 'react';
import { init, use, type ECharts, type ComposeOption } from 'echarts/core';
import { LineChart, type LineSeriesOption } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TooltipComponent,
  type GridComponentOption,
  type LegendComponentOption,
  type TooltipComponentOption,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { useThemeMode } from '../lib/useThemeMode.js';
import type { ShotSample } from '../lib/espresso.js';

use([LineChart, GridComponent, LegendComponent, TooltipComponent, CanvasRenderer]);

type Option = ComposeOption<LineSeriesOption | GridComponentOption | LegendComponentOption | TooltipComponentOption>;

/** Series colours, one hue per quantity; targets reuse their quantity's hue, dashed. */
const COLORS = {
  light: { pressure: '#0a6fd8', flow: '#1f8a4c', weight: '#b35c00', temp: '#c2255c', axis: '#6e6e73', grid: '#e5e5ea' },
  dark: { pressure: '#4ba3ff', flow: '#4cd07d', weight: '#ffa94d', temp: '#ff6b9a', axis: '#98989d', grid: '#2c2c2e' },
};

/**
 * One shot's curves, like the machine's own graph: pressure and pump flow
 * (with their dashed targets) on the left axis, weight in the cup on the
 * right. Temperature has its own scale, so it is a second right axis.
 */
export default function ShotChart({ samples }: { samples: ShotSample[] }): JSX.Element {
  const el = useRef<HTMLDivElement>(null);
  const chart = useRef<ECharts | null>(null);
  const mode = useThemeMode();

  useEffect(() => {
    if (!el.current) return;
    chart.current ??= init(el.current);
    const c = COLORS[mode];
    const sec = (s: ShotSample): number => Math.round(s[0] / 100) / 10;
    const line = (name: string, idx: number, color: string, axis: number, dashed = false): LineSeriesOption => ({
      name,
      type: 'line',
      showSymbol: false,
      yAxisIndex: axis,
      data: samples.map((s) => [sec(s), s[idx]]),
      lineStyle: { color, width: dashed ? 1.5 : 2.25, type: dashed ? 'dashed' : 'solid' },
      itemStyle: { color },
    });
    const option: Option = {
      animation: false,
      grid: { left: 36, right: 64, top: 36, bottom: 28 },
      legend: { top: 0, textStyle: { color: c.axis }, data: ['Pressure', 'Flow', 'Weight', 'Temp'] },
      tooltip: { trigger: 'axis', valueFormatter: (v) => (typeof v === 'number' ? v.toFixed(1) : String(v)) },
      xAxis: {
        type: 'value',
        min: 0,
        axisLabel: { color: c.axis, formatter: '{value}s' },
        splitLine: { show: false },
      },
      yAxis: [
        { type: 'value', min: 0, max: 12, name: 'bar · ml/s', nameTextStyle: { color: c.axis }, axisLabel: { color: c.axis }, splitLine: { lineStyle: { color: c.grid } } },
        { type: 'value', min: 0, name: 'g', nameTextStyle: { color: c.axis }, axisLabel: { color: c.axis }, splitLine: { show: false } },
        { type: 'value', min: 80, max: 100, offset: 34, name: '°C', nameTextStyle: { color: c.axis }, axisLabel: { color: c.axis }, splitLine: { show: false } },
      ],
      series: [
        line('Pressure', 1, c.pressure, 0),
        line('Target pressure', 9, c.pressure, 0, true),
        line('Flow', 2, c.flow, 0),
        line('Target flow', 8, c.flow, 0, true),
        line('Weight', 5, c.weight, 1),
        line('Temp', 4, c.temp, 2),
      ],
    };
    chart.current.setOption(option, true);
    const ro = new ResizeObserver(() => chart.current?.resize());
    ro.observe(el.current);
    return () => ro.disconnect();
  }, [samples, mode]);

  useEffect(
    () => () => {
      chart.current?.dispose();
      chart.current = null;
    },
    [],
  );

  return <div ref={el} className="shot-chart" style={{ width: '100%', height: 300 }} role="img" aria-label="Shot pressure, flow, weight and temperature over time" />;
}
